import React, { useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, Checkbox, Text, Box, HStack, Badge, Divider,
  useToast,
} from '@chakra-ui/react';
import { FiCopy } from 'react-icons/fi';
import api from '../api';

export default function TaskCloneModal({ task, isOpen, onClose, onCloned }) {
  const [cloneSubtasks, setCloneSubtasks] = useState(true);
  const [cloneNotes, setCloneNotes]       = useState(false);
  const [cloneComms, setCloneComms]       = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleClone = async () => {
    setSaving(true);
    try {
      await api.post(`/tasks/${task.id}/clone`, {
        clone_subtasks: cloneSubtasks,
        clone_notes:    cloneNotes,
        clone_comms:    cloneComms,
      });
      toast({ title: 'Task cloned', status: 'success', duration: 1800 });
      onClose();
      if (onCloned) onCloned();
    } catch(e) {
      toast({ title: 'Clone failed', status: 'error', duration: 2000 });
    } finally { setSaving(false); }
  };

  if (!task) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <HStack spacing={2}>
            <FiCopy size={16} />
            <Text>Clone Task</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box bg="blue.50" p={3} borderRadius="md" border="1px" borderColor="blue.100">
              <Text fontSize="11px" color="gray.500" fontWeight="600" textTransform="uppercase" mb={0.5}>Cloning</Text>
              <Text fontSize="13px" fontWeight="700">{task.task_title || 'Untitled Task'}</Text>
              {task.source_task_id && (
                <Badge colorScheme="purple" fontSize="8px" mt={1}>This is already a clone</Badge>
              )}
            </Box>

            <Box>
              <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>
                What to include in the clone
              </Text>
              <VStack spacing={3} align="stretch">
                <Box p={3} border="1px" borderColor="teal.200" borderRadius="md" bg="teal.50">
                  <Checkbox
                    isChecked={cloneSubtasks}
                    onChange={e => setCloneSubtasks(e.target.checked)}
                    colorScheme="teal"
                  >
                    <Box ml={1}>
                      <Text fontSize="13px" fontWeight="600" color="teal.800">Sub-tasks</Text>
                      <Text fontSize="10px" color="gray.500">All sub-tasks (reset to incomplete)</Text>
                    </Box>
                  </Checkbox>
                </Box>

                <Box p={3} border="1px" borderColor="purple.200" borderRadius="md" bg="purple.50">
                  <Checkbox
                    isChecked={cloneNotes}
                    onChange={e => setCloneNotes(e.target.checked)}
                    colorScheme="purple"
                  >
                    <Box ml={1}>
                      <Text fontSize="13px" fontWeight="600" color="purple.800">Notes</Text>
                      <Text fontSize="10px" color="gray.500">All notes attached to this task</Text>
                    </Box>
                  </Checkbox>
                </Box>

                <Box p={3} border="1px" borderColor="blue.200" borderRadius="md" bg="blue.50">
                  <Checkbox
                    isChecked={cloneComms}
                    onChange={e => setCloneComms(e.target.checked)}
                    colorScheme="blue"
                  >
                    <Box ml={1}>
                      <Text fontSize="13px" fontWeight="600" color="blue.800">Communications</Text>
                      <Text fontSize="10px" color="gray.500">All email / comms logged on this task</Text>
                    </Box>
                  </Checkbox>
                </Box>
              </VStack>
            </Box>

            <Divider />
            <Box bg="gray.50" p={2} borderRadius="md">
              <Text fontSize="10px" color="gray.500">
                The clone will always include the task title, description, and assigned companies, contacts and users.
                Dates, progress and invoices are <b>not</b> copied.
              </Text>
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" leftIcon={<FiCopy size={14} />} bg="blue.600" color="white"
            _hover={{ bg: 'blue.700' }} isLoading={saving} onClick={handleClone}>
            Clone Task
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
